export const ALERTS_DATA_CATEGORY_BY_TAB_KEY = {
  all: "all",
  vehicles: "vehicle",
  users: "user",
  expiry: "expiry",
};

export const ALERTS_PERMISSION_TYPE_BY_TAB_KEY = {
  vehicles: "vehicle",
  users: "user",
  expiry: "expiry",
};

const ALERTS_TAB_PERMISSIONS = {
  vehicles: { apiPath: "/api/alert/organization/vehicle", action: "GET" },
  users: { apiPath: "/api/alert/organization/user", action: "GET" },
  expiry: { apiPath: "/api/alert/organization/expiry", action: "GET" },
};

const LEGACY_ALERTS_TABS = [
  {
    tabKey: "all",
    labelKey: "all",
    metricKey: "totalAlerts",
    order: 0,
    permission: { apiPath: "/api/alert", action: "GET" },
  },
  {
    tabKey: "vehicles",
    labelKey: "Vehicles",
    metricKey: "vehicleAlerts",
    order: 1,
    permission: ALERTS_TAB_PERMISSIONS.vehicles,
  },
  {
    tabKey: "users",
    labelKey: "users",
    metricKey: "userAlerts",
    order: 2,
    permission: ALERTS_TAB_PERMISSIONS.users,
  },
  {
    tabKey: "expiry",
    labelKey: "expiry",
    metricKey: "expiryAlerts",
    order: 3,
    permission: ALERTS_TAB_PERMISSIONS.expiry,
  },
];

const LEGACY_ALERTS_FILTERS = [
  {
    id: "dateRange",
    type: "dateRange",
    labelKey: "Select Date Range",
    filterKey: "dateRange",
  },
  {
    id: "alertStatus",
    type: "selectFilter",
    labelKey: "All Alerts",
    filterKey: "alertStatus",
    options: [
      { value: "all", labelKey: "All Alerts" },
      { value: "unread", labelKey: "Unread" },
      { value: "read", labelKey: "Read" },
      { value: "starred", labelKey: "Starred" },
      { value: "none", labelKey: "None" },
    ],
  },
  {
    id: "vehicleFilter",
    type: "multiSelectFilter",
    labelKey: "All Vehicles",
    filterKey: "vehicle",
    visibleOnTab: "vehicles",
  },
  {
    id: "expiryType",
    type: "selectFilter",
    labelKey: "All Expiry Types",
    filterKey: "expiryType",
    visibleOnTab: "expiry",
    options: [
      { value: "", labelKey: "All Expiry Types" },
      { value: "service", labelKey: "Service Due Date" },
      { value: "insurance", labelKey: "Insurance Policy Expiry Date" },
      { value: "registration", labelKey: "Re-Registration Due Date" },
    ],
  },
];

export function computeAlertMetric(metricKey, alertCounts = {}) {
  const byType = alertCounts.byType || {};

  switch (metricKey) {
    case "totalAlerts":
      return alertCounts.total || 0;
    case "vehicleAlerts":
      return byType.VEHICLE || 0;
    case "userAlerts":
      return byType.USER || 0;
    case "expiryAlerts":
      return byType.EXPIRY || 0;
    default:
      return 0;
  }
}

export function hasAlertsTabPermission(tabKey, permission, features) {
  if (tabKey === "all") {
    return ["vehicles", "users", "expiry"].some((key) =>
      hasAlertsTabPermission(key, ALERTS_TAB_PERMISSIONS[key], features),
    );
  }

  if (!permission?.apiPath) {
    return true;
  }
  if (!features) {
    return false;
  }

  const action = permission.action || "GET";
  return features.some(
    (feature) => feature.api_path === permission.apiPath && feature.action === action,
  );
}

export function hasAlertDataPermission(permissionType, features) {
  const tabKey = Object.entries(ALERTS_PERMISSION_TYPE_BY_TAB_KEY).find(
    ([, type]) => type === permissionType,
  )?.[0];

  if (!tabKey) {
    return false;
  }

  return hasAlertsTabPermission(
    tabKey,
    ALERTS_TAB_PERMISSIONS[tabKey],
    features,
  );
}

function resolveTabLabel(tab, t) {
  return t(tab.labelKey || tab.tabKey);
}

function filterAlertsTabs(tabs, features) {
  return tabs.filter((tab) => hasAlertsTabPermission(tab.tabKey, tab.permission, features));
}

export function getLegacyAlertsTabs({ t, features }) {
  return filterAlertsTabs(LEGACY_ALERTS_TABS, features)
    .sort((a, b) => a.order - b.order)
    .map((tab) => ({
      ...tab,
      label: resolveTabLabel(tab, t),
    }));
}

export function getLegacyAlertsFilters() {
  return LEGACY_ALERTS_FILTERS;
}

export const ALERTS_BULK_ACTION_KEYS = new Set([
  "clearSelection",
  "selectedCount",
  "delete",
  "markAsRead",
  "markAsUnread",
]);

export const LEGACY_BULK_ACTIONS = [
  {
    actionKey: "clearSelection",
    labelKey: "Clear Selection",
  },
  {
    actionKey: "selectedCount",
    labelKey: "Selected",
  },
  {
    actionKey: "delete",
    labelKey: "Remove (Delete)",
    permission: { apiPath: "/api/alert/delete", action: "DELETE" },
  },
  {
    actionKey: "markAsRead",
    labelKey: "Mark as Read",
    permission: { apiPath: "/api/alert", action: "PUT" },
  },
  {
    actionKey: "markAsUnread",
    labelKey: "Mark as Unread",
    permission: { apiPath: "/api/alert", action: "PUT" },
  },
];

export function hasBulkActionPermission(permission, features) {
  if (!permission?.apiPath) {
    return true;
  }
  if (!features) {
    return false;
  }

  const action = permission.action || "GET";
  return features.some(
    (feature) => feature.api_path === permission.apiPath && feature.action === action,
  );
}

export function getBulkActionsForWidget(widget, features) {
  if (!widget || widget.type !== "bulkActions") {
    return [];
  }

  const actions =
    Array.isArray(widget.actions) && widget.actions.length > 0
      ? widget.actions
      : LEGACY_BULK_ACTIONS;

  return actions.filter((action) =>
    hasBulkActionPermission(action.permission, features),
  );
}

export function getBulkActionLabel(action, t) {
  return t(action?.labelKey || action?.actionKey || "");
}

const LEGACY_ALERTS_LIST_HEADER = [
  { id: "bulkSelect", type: "bulkSelect" },
  {
    id: "listTitle",
    type: "listTitle",
    labelKey: "Alerts",
    showCount: true,
  },
  { id: "bulkActions", type: "bulkActions", actions: LEGACY_BULK_ACTIONS },
  {
    id: "search",
    type: "search",
    placeholderKey: "search",
  },
  {
    id: "pagination",
    type: "pagination",
    pageSize: 50,
  },
  {
    id: "sort",
    type: "sort",
    labelKey: "timestamp",
    sortKey: "createdAt",
    options: [
      { value: "desc", labelKey: "Newest" },
      { value: "asc", labelKey: "Oldest" },
    ],
  },
];

export function getLegacyAlertsListHeader() {
  return LEGACY_ALERTS_LIST_HEADER;
}
